import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {gql} from '@apollo/client';
import {Apollo} from 'apollo-angular';
import {Subject, Subscription} from 'rxjs';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {
  CoachEditComponent_getCoach,
  CoachEditComponent_getCoach_user,
  CoachEditComponent_getCoachVariables,
} from './__generated__/CoachEditComponent_getCoach';
import {
  CoachEditComponent_editCoach,
  CoachEditComponent_editCoachVariables,
} from './__generated__/CoachEditComponent_editCoach';
import {notNull} from '../../../shared/utils';
import {catchError, exhaustMap} from 'rxjs/operators';
import {Language} from '../../../new-client/new-client.component';
import {CoachMutationInput, LANGUAGES} from '../../../shared/graphql/globalTypes';
import {ErrorService} from '../../../shared/services/error.service';

const GET_COACH = gql`
  query CoachEditComponent_getCoach($id: ID!) {
    user(id: $id) {
      id
      firstName
      lastName
      email
      language
      role
      isActive
    }
  }
`;

const EDIT_COACH = gql`
  mutation CoachEditComponent_editCoach($input: CoachMutationInput!) {
    createOrUpdateCoach(input: $input) {
      user {
        id
        firstName
        lastName
        email
        language
        role
        isActive
      }
    }
  }
`;

@Component({
  selector: 'app-coach-edit',
  templateUrl: './coach-edit.component.html',
  styleUrls: ['./coach-edit.component.scss'],
})
export class CoachEditComponent implements OnInit, OnDestroy {
  coach: CoachEditComponent_getCoach_user | null = null;
  private coachId: string | null = null;
  private subscriptions: Subscription[] = [];

  editForm = new FormGroup({
    id: new FormControl(null),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    email: new FormControl(null, Validators.required),
    language: new FormControl(null, Validators.required),
    isActive: new FormControl(true)
  });
  languages: Language[] = [
    Language.NL,
    // Language.EN,
    Language.DE,
  ];
  submit$ = new Subject<CoachMutationInput>();
  invalidSubmission = false;

  constructor(
    private router: Router,
    private apollo: Apollo,
    private toastr: ToastrService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private errorHandler: ErrorService,
  ) {}

  ngOnInit(): void {
    const languageTranslationMap: {[key in Language]: string} = {
      [Language.NL]: this.translate.instant('NL', Language.NL),
      // [Language.EN]: this.translate.instant('ENGLISH', Language.EN),
      [Language.DE]: this.translate.instant('DE', Language.DE),
    };

    this.languages.forEach(lang => languageTranslationMap[lang]);

    this.subscriptions.push(
      this.route.paramMap.subscribe(params => {
        this.coachId = params.get('coachId');
      }),

      this.apollo
        .watchQuery<CoachEditComponent_getCoach, CoachEditComponent_getCoachVariables>({
          query: GET_COACH,
          variables: {id: notNull(this.coachId)},
        })
        .valueChanges.pipe(catchError(error => this.errorHandler.handleApollo(error)))
        .subscribe(results => {
          this.coach = results.data.user;
          this.editForm.get('firstName')?.setValue(results.data.user?.firstName);
          this.editForm.get('lastName')?.setValue(results.data.user?.lastName);
          this.editForm.get('email')?.setValue(results.data.user?.email);
          this.editForm.get('isActive')?.setValue(results.data.user?.isActive);

          if (results.data.user?.language === LANGUAGES.nl) {
            this.editForm.controls['language'].setValue(Language.NL);
          } else if (results.data.user?.language === LANGUAGES.de) {
            this.editForm.controls['language'].setValue(Language.DE);
          }
        }),
      this.submit$
        .pipe(
          exhaustMap(input => {
            return this.apollo
              .mutate<CoachEditComponent_editCoach, CoachEditComponent_editCoachVariables>({
                mutation: EDIT_COACH,
                variables: {
                  input: input,
                },
              })
              .pipe(catchError(error => this.errorHandler.handleApollo(error)));
          }),
        )
        .subscribe((result) => {
          this.toastr.success(this.translate.instant('CHANGES_HAVE_BEEN_SAVED'), '', {
            timeOut: 2000,
          });
        }),
    );
  }

  onEdit() {
    if (this.editForm.invalid || !this.coachId) {
      this.invalidSubmission = true;
      return;
    }

    const input: CoachMutationInput = {
      ...this.editForm.value,
      id: this.coachId,
      language: this.editForm.controls['language'].value.toLowerCase(),
      isActive: this.editForm.controls['isActive'].value === 'true'
    }

    this.submit$.next(input);
  }

  returnToPreviousPage() {
    const currentUrl = this.router.url;
    const parentUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/coach'));
    this.router.navigateByUrl(parentUrl);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
